import { useState, useRef, useCallback, useLayoutEffect, useEffect, Children } from "react";
import { createPortal } from "react-dom";

const HIDE_MS = 120;

/**
 * Tooltip rendered with fixed positioning in a portal (document.body).
 * Use for icon buttons inside tables where CSS ::after tooltips sit under later rows.
 */
export default function FloatingTooltip({ label, children, anchorClassName = "inline-flex" }) {
  const anchorRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const show = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setOpen(true);
  }, []);

  const hideDelayed = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setOpen(false);
      hideTimerRef.current = null;
    }, HIDE_MS);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(
    () => () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    []
  );

  const tooltip =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed max-w-[min(16rem,70vw)] rounded px-2 py-1.5 text-center text-xs leading-snug shadow-lg"
            style={{
              top: pos.top,
              left: pos.left,
              transform: "translateX(-50%)",
              zIndex: 200000,
              backgroundColor: "var(--text-primary)",
              color: "var(--bg-primary)",
            }}
          >
            {label}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={anchorClassName}
        onMouseEnter={() => {
          show();
          Promise.resolve().then(updatePosition);
        }}
        onMouseLeave={hideDelayed}
        onFocusCapture={() => {
          show();
          Promise.resolve().then(updatePosition);
        }}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) hideDelayed();
        }}
      >
        {Children.only(children)}
      </span>
      {tooltip}
    </>
  );
}
